import { DestroyRef, Injectable, effect, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EMPTY, Subject, catchError, finalize, switchMap, timer } from 'rxjs';
import { CARD_TRANSPORT } from '../api/card.transport';
import type {
  ListOperationsParams,
  ListTransactionsParams,
  StatisticsParams,
} from '../api/card.transport';
import { cardErrorKey, isDegraded } from './card-error';
import { CardEventsService } from './card-events.service';
import { CardStore } from './card.store';
import { isProgressing } from './card.types';

const POLL_MS = 15_000;
const REFRESH_COOLDOWN_MS = 2000;

@Injectable()
export class CardSyncService {
  private readonly transport = inject(CARD_TRANSPORT);
  private readonly store = inject(CardStore);
  private readonly destroyRef = inject(DestroyRef);
  private readonly events = inject(CardEventsService);

  private polling = false;

  private lastRefreshAt = 0;

  private readonly _applyEvents = effect(() => {
    if (!this.events.stale()) return;
    this.events.consumeStale();
    const now = Date.now();
    if (now - this.lastRefreshAt < REFRESH_COOLDOWN_MS) return;
    this.lastRefreshAt = now;
    this.refreshOperations();
    this.refreshCards();
  });

  constructor() {
    timer(POLL_MS, POLL_MS)
      .pipe(
        switchMap(() => {
          const hasInFlight = this.store.operations().some(op => isProgressing(op.status));
          if (!hasInFlight || this.polling) return EMPTY;
          this.polling = true;
          return this.transport.operations({ take: 20 }).pipe(
            catchError(() => EMPTY),
            finalize(() => {
              this.polling = false;
            }),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(page => this.store.mergeOperations(page.data));
  }

  loadCards(): void {
    this.store.setLoading(true);
    this.transport
      .list({ take: 20 })
      .pipe(
        catchError((error: unknown) => {
          this.store.setLoading(false);
          this.store.setError(cardErrorKey(error), isDegraded(error));
          return EMPTY;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(page => {
        this.store.clearError();
        this.store.setCards(page.data);
      });
  }

  loadOperations(params?: ListOperationsParams): void {
    this.transport
      .operations({ take: 20, ...params })
      .pipe(
        catchError((error: unknown) => {
          this.store.setError(cardErrorKey(error), isDegraded(error));
          return EMPTY;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(page => this.store.setOperations(page.data));
  }

  loadTransactions(params: ListTransactionsParams): void {
    this.transactionRequest$.next(params);
  }

  private readonly transactionRequest$ = new Subject<ListTransactionsParams>();

  private readonly _transactions = this.transactionRequest$
    .pipe(
      switchMap(params =>
        this.transport.transactions({ take: 20, ...params }).pipe(catchError(() => EMPTY)),
      ),
      takeUntilDestroyed(this.destroyRef),
    )
    .subscribe(page => this.store.setTransactions(page.data, page.count));

  loadStatistics(params?: StatisticsParams): void {
    this.transport
      .statistics(params)
      .pipe(
        catchError(() => EMPTY),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(statistics => this.store.setStatistics(statistics));
  }

  private refreshCards(): void {
    this.transport
      .list({ take: 20 })
      .pipe(
        catchError(() => EMPTY),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(page => this.store.setCards(page.data));
  }

  private refreshOperations(): void {
    this.transport
      .operations({ take: 20 })
      .pipe(
        catchError(() => EMPTY),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(page => this.store.mergeOperations(page.data));
  }

  refreshCard(cardId: string): void {
    this.transport
      .get(cardId)
      .pipe(
        catchError(() => EMPTY),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(card => this.store.upsertCard(card));
  }
}
